/**
 * Algolia Insights 이벤트(검색 결과 클릭 · 상품 조회 · 장바구니 · 구매).
 *
 * Personalization 은 검색 요청이 아니라 이 이벤트로 고객 취향을 학습한다. 검색 요청과 같은 userToken(고객 ID)으로
 * 보내야 이어지므로 비로그인이면 보내지 않는다. 인덱스가 변형 단위라 objectID 는 Shopify 변형 ID 숫자와 같다.
 *
 * algoliasearch 를 불러오지 않는다. useTracking 을 거쳐 Algolia 를 쓰지 않는 스토어 번들에도 들어가기 때문이다.
 * (configureAlgolia 가 호출되지 않은 스토어에서는 모든 함수가 아무것도 하지 않는다.)
 */

const INSIGHTS_URL = "https://insights.algolia.io/1/events";

/** Insights API 가 이벤트 하나에 받는 objectID 최대 개수 */
const MAX_OBJECT_IDS = 20;

/** 검색에서 누른 상품의 queryID 를 장바구니 · 구매 이벤트까지 이어 주는 sessionStorage 키 */
const QUERY_ID_STORAGE_KEY = "algolia-insights-query-ids";

/** 기억해 두는 상품 수. 오래된 것부터 버린다. */
const MAX_REMEMBERED = 50;

interface IInsightsConfig {
  appId: string;
  searchKey: string;
  /** 이벤트를 연결할 인덱스. 상품 레코드가 있는 기본 인덱스를 쓴다. */
  indexName: string;
}

/** 검색 결과 카드 하나를 누를 때 필요한 값 */
export interface IAlgoliaClickTarget {
  /** 카드의 대표 변형 레코드 objectID */
  objectID: string;
  /** 상품 ID 숫자. 장바구니 · 구매 때 이 상품이 어느 검색에서 왔는지 찾는 키 */
  productId: string;
  /** clickAnalytics 로 받은 검색의 queryID */
  queryID?: string;
  /** 화면에 보인 순서(1부터) */
  position: number;
}

let config: IInsightsConfig | null = null;
let userToken: string | undefined;

/** 같은 검색 결과를 여러 번 눌러도(색상 칩 등) 클릭은 한 번만 보낸다. */
const sentClicks = new Set<string>();

/** client.ts 의 configureAlgolia() 가 호출한다. */
export function configureInsights(next: IInsightsConfig) {
  config = next.appId && next.searchKey && next.indexName ? next : null;
}

/** 금액은 소수점 둘째 자리까지 보낸다(할인가 ÷ 수량처럼 나누어떨어지지 않는 값 대비). */
const toMoney = (amount: number) => Math.round(amount * 100) / 100;

/** gid://shopify/Product/123 → "123" */
function toNumericId(id?: string | null) {
  return id?.split("/").pop() ?? "";
}

/**
 * 고객 ID(gid://shopify/Customer/123) → userToken("123"). 로그아웃이면 빈 값을 넘긴다.
 * userToken 은 영문 · 숫자와 = + / - _ 만 쓸 수 있어(최대 129자) gid 를 그대로 보낼 수 없다.
 */
export function setAlgoliaUserToken(customerId?: string | null) {
  const id = toNumericId(customerId);
  userToken = /^[\w=+/-]{1,129}$/.test(id) ? id : undefined;
}

export function getAlgoliaUserToken(): string | undefined {
  return userToken;
}

function send(events: Record<string, unknown>[]) {
  if (!config || !userToken || events.length === 0) return;

  const { appId, searchKey, indexName } = config;
  const url = `${INSIGHTS_URL}?X-Algolia-Application-Id=${encodeURIComponent(appId)}&X-Algolia-API-Key=${encodeURIComponent(searchKey)}`;
  const body = JSON.stringify({
    events: events.map((event) => ({ index: indexName, userToken, ...event })),
  });

  try {
    // 상품 페이지로 넘어가거나 결제 완료 후 페이지를 떠나도 전송되도록 sendBeacon 을 먼저 쓴다.
    // 문자열 본문은 text/plain 이라 CORS 사전 요청 없이 나간다(Insights API 가 받는 형식).
    if (navigator.sendBeacon(url, body)) return;
  } catch {
    // sendBeacon 을 못 쓰는 환경이면 아래 fetch 로 보낸다.
  }

  // 이벤트 전송 실패가 화면 동작에 영향을 주지 않게 무시한다.
  void fetch(url, { method: "POST", body, keepalive: true }).catch(() => undefined);
}

function readQueryIDs(): [string, string][] {
  try {
    const raw = sessionStorage.getItem(QUERY_ID_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as [string, string][]) : [];
  } catch {
    return [];
  }
}

function rememberQueryID(productId: string, queryID: string) {
  try {
    const next = [...readQueryIDs().filter(([id]) => id !== productId), [productId, queryID]].slice(-MAX_REMEMBERED);
    sessionStorage.setItem(QUERY_ID_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // sessionStorage 를 못 쓰는 환경이면 검색 연결 없이 이벤트만 보낸다.
  }
}

function queryIDOf(productId?: string | null): string | undefined {
  const id = toNumericId(productId);
  if (!id) return undefined;
  return readQueryIDs().find(([remembered]) => remembered === id)?.[1];
}

/** Algolia hit(useHits · useInfiniteHits 의 item) → 클릭 대상. position 을 주지 않으면 hit 의 순위를 쓴다. */
export function toAlgoliaClickTarget(
  hit: { objectID: string; id?: number | string; __queryID?: string; __position?: number },
  position?: number,
): IAlgoliaClickTarget {
  return {
    objectID: hit.objectID,
    productId: toNumericId(String(hit.id ?? "")),
    queryID: hit.__queryID,
    position: position ?? hit.__position ?? 1,
  };
}

/** 검색 결과(검색 페이지 · 헤더 드롭다운)에서 상품을 눌렀을 때 */
export function trackAlgoliaClick(target?: IAlgoliaClickTarget) {
  if (!target || !config || !userToken) return;

  const key = `${target.queryID ?? ""}:${target.objectID}`;
  if (sentClicks.has(key)) return;
  sentClicks.add(key);

  if (target.queryID) {
    if (target.productId) rememberQueryID(target.productId, target.queryID);
    send([
      {
        eventType: "click",
        eventName: "Product Clicked",
        objectIDs: [target.objectID],
        queryID: target.queryID,
        positions: [target.position],
      },
    ]);
    return;
  }

  send([{ eventType: "click", eventName: "Product Clicked", objectIDs: [target.objectID] }]);
}

/** 검색 결과 노드에 클릭 대상을 싣는다. 화면은 onClick 에서 trackAlgoliaClickOn(node) 로 꺼내 보낸다. */
export function withAlgoliaClick<T extends object>(node: T, target: IAlgoliaClickTarget): T {
  return { ...node, algoliaClick: target };
}

/** 검색 결과 카드를 눌렀을 때. Algolia 에서 온 노드가 아니면(Shopify 폴백) 아무것도 하지 않는다. */
export function trackAlgoliaClickOn(node: unknown) {
  trackAlgoliaClick((node as { algoliaClick?: IAlgoliaClickTarget } | null | undefined)?.algoliaClick);
}

/** 상품 상세를 봤을 때. 상품의 첫 변형(대표 변형)으로 보낸다. 스토어마다 variants 조회 여부가 달라 느슨하게 받는다. */
export function trackAlgoliaView(product: { variants?: { edges?: { node?: { id?: string } | null }[] } | null }) {
  const objectID = toNumericId(product.variants?.edges?.[0]?.node?.id);
  if (!objectID) return;

  send([{ eventType: "view", eventName: "Product Viewed", objectIDs: [objectID] }]);
}

/** 장바구니에 담았을 때. 검색에서 누른 상품이면 그 검색(queryID)에 연결한다. */
export function trackAlgoliaAddToCart({
  variantId,
  productId,
  price,
  currency,
  quantity = 1,
}: {
  variantId?: string | null;
  productId?: string | null;
  price: number;
  currency: string;
  quantity?: number;
}) {
  const objectID = toNumericId(variantId);
  if (!objectID) return;

  const queryID = queryIDOf(productId);

  send([
    {
      eventType: "conversion",
      eventSubtype: "addToCart",
      eventName: "Product Added to Cart",
      objectIDs: [objectID],
      objectData: [{ price: toMoney(price), quantity }],
      value: toMoney(price * quantity),
      currency,
      ...(queryID ? { queryID } : {}),
    },
  ]);
}

/** 주문을 완료했을 때. 상품마다 검색에서 왔으면 그 검색(queryID)에 연결한다. */
export function trackAlgoliaPurchase(
  items: { variantId?: string | null; productId?: string | null; price: number; quantity: number }[],
  currency: string,
) {
  const lines = items
    .map((item) => ({ ...item, objectID: toNumericId(item.variantId), queryID: queryIDOf(item.productId) }))
    .filter((item) => item.objectID);

  const events = [];
  for (let start = 0; start < lines.length; start += MAX_OBJECT_IDS) {
    const chunk = lines.slice(start, start + MAX_OBJECT_IDS);
    events.push({
      eventType: "conversion",
      eventSubtype: "purchase",
      eventName: "Product Purchased",
      objectIDs: chunk.map((line) => line.objectID),
      objectData: chunk.map((line) => ({
        price: toMoney(line.price),
        quantity: line.quantity,
        ...(line.queryID ? { queryID: line.queryID } : {}),
      })),
      value: toMoney(chunk.reduce((sum, line) => sum + line.price * line.quantity, 0)),
      currency,
    });
  }

  send(events);
}
